import type { IframeLoginConfig, OAuth2PKCEConfig, PopupLoginConfig } from "../types";

const MAX_URL_BYTES = 2048;
const MAX_FIELD_BYTES = 256;
const MAX_RESOURCE_BYTES = 128;
const RESOURCE_PATTERN = /^[A-Za-z0-9._:/-]+$/;
const DEFAULT_IFRAME_SANDBOX = "allow-forms allow-scripts allow-same-origin";
const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;
const MAX_REQUEST_TIMEOUT_MS = 30_000;

export function validateOAuth2PKCEConfig(config: OAuth2PKCEConfig): void {
  validateOAuth2Base(config);
}

export function validatePopupLoginConfig(config: PopupLoginConfig): void {
  validateOAuth2Base(config);
  if (config.timeoutMs !== undefined) {
    validatePositiveInteger(config.timeoutMs, "timeoutMs");
  }
  if (config.popup) {
    validateOptionalPositiveInteger(config.popup.width, "popup.width");
    validateOptionalPositiveInteger(config.popup.height, "popup.height");
    if (config.popup.left !== undefined) {
      validateFiniteNumber(config.popup.left, "popup.left");
    }
    if (config.popup.top !== undefined) {
      validateFiniteNumber(config.popup.top, "popup.top");
    }
  }
}

export function validateIframeLoginConfig(config: IframeLoginConfig): void {
  validateOAuth2Base(config);
  if (config.timeoutMs !== undefined) {
    validatePositiveInteger(config.timeoutMs, "timeoutMs");
  }
  if (config.iframe?.sandbox !== undefined) {
    if (config.iframe.sandbox.includes("allow-top-navigation")) {
      throw new Error("iframe.sandbox must not contain allow-top-navigation");
    }
  }
  if (config.iframe?.title !== undefined) {
    validateRequiredText(config.iframe.title, "iframe.title");
  }
}

function validateOAuth2Base(config: Omit<OAuth2PKCEConfig, "kind">): void {
  validateHttpUrl(config.idaasBaseUrl, "idaasBaseUrl");
  if (config.issuer !== undefined) {
    validateHttpUrl(config.issuer, "issuer");
    if (new URL(config.issuer).origin !== config.issuer) {
      throw new Error("issuer must be an absolute origin without path, query, or fragment");
    }
  }
  validateRequiredText(config.clientId, "clientId");
  validateResource(config.resource);
  validateHttpUrl(config.redirectUri, "redirectUri");
  if (config.requestTimeoutMs !== undefined) {
    validatePositiveInteger(config.requestTimeoutMs, "requestTimeoutMs");
    if (config.requestTimeoutMs > MAX_REQUEST_TIMEOUT_MS) {
      throw new Error(`requestTimeoutMs must be at most ${MAX_REQUEST_TIMEOUT_MS}`);
    }
  }
}

function validateResource(value: string): void {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error("resource is required");
  }
  if (new TextEncoder().encode(value).length > MAX_RESOURCE_BYTES) {
    throw new Error(`resource must be at most ${MAX_RESOURCE_BYTES} bytes`);
  }
  if (!RESOURCE_PATTERN.test(value)) {
    throw new Error("resource contains unsupported characters");
  }
}

function validateHttpUrl(value: string, fieldName: string): void {
  validateRequiredText(value, fieldName, MAX_URL_BYTES);

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new Error(`${fieldName} must be an absolute HTTP(S) URL`);
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    throw new Error(`${fieldName} must use http or https`);
  }
}

function validateRequiredText(value: string, fieldName: string, maxBytes = MAX_FIELD_BYTES): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${fieldName} is required`);
  }
  if (new TextEncoder().encode(value).length > maxBytes) {
    throw new Error(`${fieldName} must be at most ${maxBytes} bytes`);
  }
  if (hasControlCharacter(value)) {
    throw new Error(`${fieldName} must not contain control characters`);
  }
}

function validatePositiveInteger(value: number, fieldName: string): void {
  if (!Number.isInteger(value) || value <= 0) {
    throw new Error(`${fieldName} must be a positive integer`);
  }
}

function validateOptionalPositiveInteger(value: number | undefined, fieldName: string): void {
  if (value === undefined) return;
  validatePositiveInteger(value, fieldName);
}

function validateFiniteNumber(value: number, fieldName: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${fieldName} must be a finite number`);
  }
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index++) {
    const code = value.charCodeAt(index);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

export const DEFAULT_IFRAME_SANDBOX_VALUE = DEFAULT_IFRAME_SANDBOX;

export function resolveRequestTimeout(value: number | undefined): number {
  return value ?? DEFAULT_REQUEST_TIMEOUT_MS;
}
